import * as React from 'react';
import * as styles from './TextField.css';
import { IATooltip } from '../tooltip/Tooltip';
import { IAIcon } from '../icon/Icon';
import { LocalizationService } from '../../services/LocalizationService';

let instanceCounter = 0;

export interface ITextFieldProps {
  id?: string;
  label?: string;
  text: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  secure?: boolean;
  style?: React.CSSProperties;
  inputFieldStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  darkMode?: boolean;
  borderRadius?: number;
  onChange: (text: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  toolTipText?: string;
  placeholderIcon?: string
  highlightColor?: string;
  placeholderColor?: string;
  onlyPaste?: boolean;
  showClearTextButton?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  onEnterKeyPressed?: () => void;
  clearTextCallback?: () => void;
}

export interface ITextFieldState {
  text: string;
  showTooLongTextErrorMessage: boolean;
}

export class IATextField extends React.Component<ITextFieldProps, ITextFieldState> {

  private inputRef: HTMLInputElement;
  private autoFocusTimeout: number;
  private autoFocusAttempts: number = 0;
  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly instanceId: string;

  constructor(props: ITextFieldProps) {
    super(props);
    this.instanceId = props.id || `ia-textfield-${++instanceCounter}`;
    this.state = {
      text: props.text,
      showTooLongTextErrorMessage: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    this.focusInputIfNeeded();
  }

  public componentDidUpdate(prevProps: ITextFieldProps): void {
    const autoFocusTurnedOn = !prevProps.autoFocus && this.props.autoFocus;
    const inputWasEnabled = prevProps.disabled && !this.props.disabled;
    if (autoFocusTurnedOn || inputWasEnabled) {
      this.autoFocusAttempts = 0;
      this.focusInputIfNeeded();
    }
  }

  public componentWillUnmount(): void {
    if (this.autoFocusTimeout) {
      clearTimeout(this.autoFocusTimeout);
    }
  }

  public componentWillReceiveProps(props: ITextFieldProps): void {
    this.setState({ text: props.text });
  }

  private focusInputIfNeeded(): void {
    if (!this.props.autoFocus || this.props.disabled || !this.inputRef) {
      return;
    }

    if (document.activeElement === this.inputRef) {
      return;
    }

    // Retry a few times in case another component steals focus during initial paint.
    if (this.autoFocusAttempts >= 3) {
      return;
    }

    this.autoFocusAttempts += 1;
    this.inputRef.focus();

    if (document.activeElement !== this.inputRef) {
      this.autoFocusTimeout = window.setTimeout(() => {
        this.focusInputIfNeeded();
      }, 80);
    }
  }

  private onChange(text: string): void {
    if (this.props.maxLength == undefined || text?.length < this.props.maxLength) {
      this.setState({ text, showTooLongTextErrorMessage: false });
      this.props.onChange(text);
    } else {
      this.setState({ showTooLongTextErrorMessage: true });
    }
  }

  private onChangeOnlyPaste(event) {
    // only allow delete beside pasting
    var inputType = event.nativeEvent.inputType;
    if (inputType == "deleteContentBackward" || inputType == "deleteContentForward") {
      var text = event.target.value;
      this.onChange(text);
    }
  }

  private onPaste(event) {
    const text = event.clipboardData.getData("text/plain");
    this.onChange(text);
  }

  private onCut(event) {
    var text = event.currentTarget.value;
    event.clipboardData.setData("text/plain", text);
    text = "";
    this.onChange(text); // clear state
    event.preventDefault();
  }

  public render(): JSX.Element {
    const inputId = this.props.id || this.instanceId;
    let label;
    if (this.props.label) {
      label = <label htmlFor={inputId} style={{ float: "left", ...this.props.labelStyle }}>{this.props.label}</label>;
      if (this.props.required) {
        label = <label htmlFor={inputId} style={{ float: "left", ...this.props.labelStyle }}>{this.props.label} <span style={{ color: "red" }}>*</span></label>;
      }
    }
    const style: React.CSSProperties = { ...(this.props.style ?? {}) };
    if (!style["--highlight-color"]) {
      style["--highlight-color"] = this.props.highlightColor ?? "black";
    }
    if (this.props.placeholderColor) {
      style["--placeholder-color"] = this.props.placeholderColor;
    }
    return (
      <div
        className={(this.props.darkMode ? styles.IA_textFieldDarkMode : styles.IA_textField) + " " + (!this.props.className ? this.props.className : "")}
        style={style}>
        {this.props.label &&
          label
        }
        {this.props.toolTipText &&
          <IATooltip
            content="Tooltip"
            type="bubble"
            rootStyles={{
              marginTop: 2,
              marginLeft: 5
            }}
            componentContent={(
              <div>{this.props.toolTipText}</div>
            )}
          >
            <IAIcon
              title="Info"
              size={14}
              color="Black"
            />
          </IATooltip>
        }
        <input
          id={inputId}
          aria-label={!this.props.label ? (this.props.placeholder || undefined) : undefined}
          ref={(ref) => this.inputRef = ref}
          type={this.props.secure ? "password" : "text"}
          placeholder={this.props.placeholder}
          value={this.state.text != undefined ? this.state.text : ""}
          onChange={(event) => this.props.onlyPaste ? this.onChangeOnlyPaste(event) : this.onChange(event.target.value)}
          onBlur={this.props.onBlur ? () => this.props.onBlur() : undefined}
          onFocus={this.props.onFocus ? () => this.props.onFocus() : undefined}
          disabled={this.props.disabled}
          autoFocus={this.props.autoFocus}
          style={{
            borderRadius: this.props.borderRadius,
            paddingRight: this.props.showClearTextButton ? 20 : undefined,
            ...this.props.inputFieldStyle
          }}
          max={this.props.maxLength}
          onPaste={this.props.onlyPaste ? (event) => this.onPaste(event) : undefined}
          onCut={this.props.onlyPaste ? (event) => this.onCut(event) : undefined}
          onKeyDown={(event) => {
            if (this.props.onEnterKeyPressed && event.key === "Enter") {
              this.props.onEnterKeyPressed();
            }
          }}
        />
        {this.props.placeholderIcon && !this.state.text &&
          <div className={styles.IA_placeholderIcon}>
            <IAIcon
              title={this.props.placeholderIcon}
              size={14}
              color={this.props.darkMode ? '#a8a8a8' : 'black'}
            />
          </div>
        }
        {this.props.showClearTextButton && this.state.text &&
          <div
            style={{
              position: "absolute",
              right: 10,
              height: this.inputRef?.getBoundingClientRect().height,
              display: "flex",
              alignItems: "center",
              bottom: 0
            }}
          >
            <IAIcon
              url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Dismiss/SVG/ic_fluent_dismiss_48_regular.svg"
              size={13}
              color={this.props.highlightColor}
              style={{
                marginTop: 3,
              }}
              onClick={() => {
                if (this.props.clearTextCallback) {
                  this.props.clearTextCallback();
                } else {
                  this.onChange("");
                }
              }}
            />
          </div>
        }
        {this.state.showTooLongTextErrorMessage &&
          <div
            style={{
              float: "left",
              width: "100%",
              color: "red",
              marginTop: 4,
              fontSize: 12
            }}
          >
            {this.localizationService.strings.Admin_ErrorMessageTooLongText?.replace("%%", `${this.props.maxLength}`)}
          </div>
        }
      </div>
    );
  }
}